import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { signIn, profile, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user && ["admin", "dispatcher"].includes(profile?.role)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [loading, user, profile, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const bundle = await signIn(email, password);
      if (!["admin", "dispatcher"].includes(bundle.profile?.role)) {
        throw new Error("This account is not authorized for the operations dashboard.");
      }
      navigate("/admin/dashboard", { replace: true });
    } catch (loginError) {
      setError(loginError.message || "Unable to sign in right now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-16 text-white flex items-center justify-center">
      <div className="w-full max-w-md rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top,#1a1a1a,#090909_70%)] p-8 shadow-[0_32px_90px_rgba(0,0,0,0.35)]">
        <div className="mb-8 text-center">
          <img src="/logo-white.png" alt="BlackRide" className="h-10 w-auto mx-auto object-contain" />
          <p className="mt-6 text-xs uppercase tracking-[0.34em] text-[#C7A239]">Operations portal</p>
          <h1 className="mt-3 font-[Playfair_Display] text-3xl">Admin & Dispatcher access</h1>
          <p className="mt-3 text-sm text-gray-400">Manage bookings, quotes, fleet assignments, drivers, payments, and reports from one place.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block space-y-2 text-sm text-gray-300">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60"
              placeholder="ops@getblackride.com"
            />
          </label>

          <label className="block space-y-2 text-sm text-gray-300">
            <span>Password</span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 pr-12 text-sm text-white outline-none focus:border-[#C7A239]/60"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>

          {error && <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C7A239] px-5 py-3.5 text-sm font-semibold text-black transition hover:bg-[#D4B34A] disabled:opacity-60"
          >
            <LogIn className="h-4 w-4" />
            {submitting ? "Signing in..." : "Enter dashboard"}
          </button>
        </form>
      </div>
    </div>
  );
}
